import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { DragDropContext, Droppable, Draggable, type DropResult } from '@hello-pangea/dnd';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { Link as LinkIcon, Plus, Trash2, Pencil, ExternalLink, Loader2, GripVertical } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { projectLinkService } from '@/services/project-link.service';
import type { ProjectLink } from '@/types/api';

interface ProjectLinksManagerProps {
  projectId: string;
}

interface LinkFormState {
  title: string;
  url: string;
  description: string;
}

const emptyForm: LinkFormState = { title: '', url: '', description: '' };

export function ProjectLinksManager({ projectId }: ProjectLinksManagerProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const queryKey = ['project-links', projectId];

  const { data: links = [] } = useQuery<ProjectLink[]>({
    queryKey,
    queryFn: () => projectLinkService.list(projectId),
    enabled: !!projectId,
  });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<ProjectLink | null>(null);
  const [form, setForm] = useState<LinkFormState>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [linkToDelete, setLinkToDelete] = useState<ProjectLink | null>(null);

  const openAdd = () => {
    setEditing(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (link: ProjectLink) => {
    setEditing(link);
    setForm({
      title: link.title,
      url: link.url,
      description: link.description ?? '',
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.title.trim() || !form.url.trim()) {
      toast({ title: 'Title and URL are required', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        url: form.url.trim(),
        description: form.description.trim() || null,
      };
      if (editing) {
        await projectLinkService.update(projectId, editing.id, payload);
        toast({ title: 'Link updated' });
      } else {
        await projectLinkService.create(projectId, payload);
        toast({ title: 'Link added' });
      }
      setDialogOpen(false);
      queryClient.invalidateQueries({ queryKey });
    } catch (error: any) {
      toast({
        title: editing ? 'Error updating link' : 'Error adding link',
        description: error?.response?.data?.error || error?.message,
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!linkToDelete) return;
    const target = linkToDelete;
    setLinkToDelete(null);
    try {
      await projectLinkService.delete(projectId, target.id);
      toast({ title: 'Link deleted' });
      queryClient.invalidateQueries({ queryKey });
    } catch (error: any) {
      toast({
        title: 'Error deleting link',
        description: error?.response?.data?.error || error?.message,
        variant: 'destructive',
      });
    }
  };

  const handleDragEnd = async (result: DropResult) => {
    if (!result.destination) return;
    if (result.destination.index === result.source.index) return;

    const reordered = Array.from(links);
    const [moved] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, moved);

    // Optimistic update
    queryClient.setQueryData<ProjectLink[]>(queryKey, reordered);

    try {
      await projectLinkService.reorder(projectId, reordered.map(l => l.id));
    } catch (error: any) {
      // Revert on failure
      queryClient.setQueryData<ProjectLink[]>(queryKey, links);
      toast({
        title: 'Error reordering',
        description: error?.response?.data?.error || error?.message,
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <LinkIcon className="h-5 w-5 text-primary" />
          <h2 className="text-lg font-semibold">Links</h2>
          <Badge variant="secondary">{links.length}</Badge>
        </div>
        <Button size="sm" onClick={openAdd}>
          <Plus className="h-4 w-4 mr-1" />
          Add Link
        </Button>
      </div>

      {links.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <LinkIcon className="h-10 w-10 mx-auto text-muted-foreground/40 mb-3" />
            <p className="text-sm text-muted-foreground mb-4">
              No links yet. Add URLs to docs, deployments, SharePoint folders, or anything related to this project.
            </p>
            <Button variant="outline" size="sm" onClick={openAdd}>
              <Plus className="h-4 w-4 mr-1" />
              Add the first link
            </Button>
          </CardContent>
        </Card>
      ) : (
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="links">
            {(provided) => (
              <div ref={provided.innerRef} {...provided.droppableProps} className="space-y-2">
                {links.map((link, index) => (
                  <Draggable key={link.id} draggableId={link.id} index={index}>
                    {(dragProvided, dragSnapshot) => (
                      <Card
                        ref={dragProvided.innerRef}
                        {...dragProvided.draggableProps}
                        className={`transition-shadow ${dragSnapshot.isDragging ? 'shadow-lg ring-2 ring-primary/30' : ''}`}
                      >
                        <CardHeader className="py-3">
                          <div className="flex items-start gap-3">
                            <div
                              {...dragProvided.dragHandleProps}
                              className="pt-1 text-muted-foreground/50 hover:text-muted-foreground cursor-grab active:cursor-grabbing"
                              title="Drag to reorder"
                            >
                              <GripVertical className="h-4 w-4" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <a
                                href={link.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="font-medium hover:underline inline-flex items-center gap-1.5 break-all"
                              >
                                {link.title}
                                <ExternalLink className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
                              </a>
                              <div className="text-xs text-muted-foreground font-mono mt-0.5 break-all">{link.url}</div>
                              {link.description && (
                                <div className="text-sm text-muted-foreground mt-1.5">{link.description}</div>
                              )}
                            </div>
                            <div className="flex items-center gap-1 flex-shrink-0">
                              <Button variant="ghost" size="icon" onClick={() => openEdit(link)} title="Edit">
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => setLinkToDelete(link)}
                                className="text-destructive hover:text-destructive hover:bg-destructive/10"
                                title="Delete"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        </CardHeader>
                      </Card>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Link' : 'Add Link'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="link-title">Title</Label>
              <Input
                id="link-title"
                placeholder="Production site"
                value={form.title}
                onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="link-url">URL</Label>
              <Input
                id="link-url"
                placeholder="https://example.com"
                value={form.url}
                onChange={e => setForm(f => ({ ...f, url: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="link-description">Description (optional)</Label>
              <Textarea
                id="link-description"
                placeholder="Short context about this link"
                value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
              {editing ? 'Save' : 'Add Link'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!linkToDelete}
        onOpenChange={() => setLinkToDelete(null)}
        title="Delete Link"
        description={`Are you sure you want to delete "${linkToDelete?.title}"? This cannot be undone.`}
        onConfirm={handleDelete}
      />
    </div>
  );
}
